/**
 * LOUD/QUIET Alert — bordered status strip with a hard shadow. Tone sets the fill; state is
 * never encoded by colour alone, so the strip always leads with a glyph plus a bold title.
 *
 * Ported 1:1 from the compiled `_ds_bundle.js`.
 *
 * Non-obvious decisions:
 * - `role="status"` (a polite live region) is set before `...rest`, matching the bundle, so a
 *   caller can upgrade it to role="alert" for genuinely assertive messages.
 * - The glyph is aria-hidden — it is redundant with the title for screen readers but is the
 *   non-colour channel for sighted users.
 * - `title` is a ReactNode here, so React.HTMLAttributes' string `title` is omitted from the
 *   inherited attribute set.
 */
import * as React from 'react';

export type AlertTone = 'good' | 'warn' | 'danger' | 'info';

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Bold, non-wrapping lead-in. Rendered next to the tone glyph. */
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Fill + glyph pairing. @default "good" */
  tone?: AlertTone;
  /** Override the tone's default glyph. Typographic glyphs only — no emoji, no SVG. */
  icon?: React.ReactNode;
  style?: React.CSSProperties;
}

const TONES: Record<AlertTone, { bg: string; glyph: string }> = {
  good: { bg: 'var(--lq-mint)', glyph: '✓' },
  warn: { bg: 'var(--lq-yellow)', glyph: '!' },
  danger: { bg: 'var(--lq-red)', glyph: '×' },
  info: { bg: 'var(--lq-blue)', glyph: 'i' },
};

export function Alert({ title, children, tone = 'good', icon, style, ...rest }: AlertProps) {
  const { bg, glyph } = TONES[tone];
  // Only the two dark fills invert their text.
  const fg = tone === 'danger' || tone === 'info' ? 'var(--lq-white)' : 'var(--lq-ink)';

  return (
    <div
      role="status"
      {...rest}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'baseline',
        border: 'var(--lq-border)',
        background: bg,
        color: fg,
        padding: 14,
        boxShadow: 'var(--lq-shadow-s)',
        fontFamily: 'var(--lq-font-sans)',
        fontSize: 13,
        ...style,
      }}
    >
      {/* NOTE: fontWeight 950 is the --lq-weight-ultra "display" weight, used here on a 13px
          run. Bundle wins; Geist ships the variable axis so it renders. */}
      <b style={{ whiteSpace: 'nowrap', fontWeight: 950 }}>
        <span aria-hidden="true">{icon || glyph}</span> {title}
      </b>
      {children ? <span>{children}</span> : null}
    </div>
  );
}

export default Alert;
